"use client";

import { useState } from 'react';
import { useCapabilities, Capability } from '@/hooks/useCapabilities';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, GripVertical, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export default function AdminCapabilitiesPage() {
  const { capabilities, isLoading, refetch } = useCapabilities();
  const { toast } = useToast();
  
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState<Capability | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [form, setForm] = useState({ name: '', display_order: 0 });

  const handleCreate = async () => {
    try {
      const { error } = await supabase
        .from('capabilities')
        .insert({
          name: form.name,
          display_order: form.display_order || capabilities.length + 1,
        });

      if (error) throw error;

      toast({ title: "Success", description: "Capability created successfully" });
      setShowDialog(false);
      setForm({ name: '', display_order: 0 });
      refetch();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleUpdate = async () => {
    if (!editing) return;

    try {
      const { error } = await supabase
        .from('capabilities')
        .update({ name: form.name, display_order: form.display_order })
        .eq('id', editing.id);

      if (error) throw error;

      toast({ title: "Success", description: "Capability updated successfully" });
      setShowDialog(false);
      setEditing(null);
      setForm({ name: '', display_order: 0 });
      refetch();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm('Are you sure you want to deactivate this capability?')) return;

    try {
      const { error } = await supabase
        .from('capabilities')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      toast({ title: "Success", description: "Capability deactivated" });
      refetch();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const openEdit = (capability: Capability) => {
    setEditing(capability);
    setForm({ name: capability.name, display_order: capability.display_order });
    setShowDialog(true);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', display_order: capabilities.length + 1 });
    setShowDialog(true);
  };

  const filteredCapabilities = capabilities.filter(cap =>
    cap.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return <div className="container mx-auto p-6">Loading...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Capability Management</h1>
        <p className="text-muted-foreground">Manage vendor capabilities</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Capabilities</CardTitle>
              <CardDescription>{capabilities.length} capabilities</CardDescription>
            </div>
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add Capability
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search capabilities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="space-y-2">
            {filteredCapabilities.map((capability) => (
              <div
                key={capability.id}
                className="p-3 border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{capability.name}</div>
                      <div className="text-xs text-muted-foreground">Order: {capability.display_order}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(capability)}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeactivate(capability.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {filteredCapabilities.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery ? 'No capabilities match your search' : 'No capabilities yet'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Capability Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Capability' : 'Create Capability'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update the capability details' : 'Add a new capability'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="capability-name">Capability Name</Label>
              <Input
                id="capability-name"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Offset Printing"
              />
            </div>
            <div>
              <Label htmlFor="capability-order">Display Order</Label>
              <Input
                id="capability-order"
                type="number"
                value={form.display_order}
                onChange={(e) => setForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button onClick={editing ? handleUpdate : handleCreate}>
                {editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
